import { QuickQuoteResponseQuote } from 'lib/api/getQuickQuote';
import { Maybe, Undef } from 'types/UtilityTypes';
import { Quoting } from 'types/enums/Integrations';

import { QuoteTypeInSource, SelectedQuoteType } from '../types';
import { TMS_LANE_HISTORY_SOURCE } from './selectDefaultQuoteCard';

type QuoteCardSource = Omit<QuickQuoteResponseQuote, 'source'> & {
  source: Quoting | typeof TMS_LANE_HISTORY_SOURCE;
};

type GetSelectedQuickQuoteIdArgs = {
  quotes: Undef<QuickQuoteResponseQuote[]>;
  selectedQuote: Maybe<SelectedQuoteType>;
  isKitCoachSelected: boolean;
};

export function getSelectedQuickQuoteId({
  quotes,
  selectedQuote,
  isKitCoachSelected,
}: GetSelectedQuickQuoteIdArgs): Undef<number> {
  if (!quotes) {
    return undefined;
  }

  if (isKitCoachSelected) {
    return quotes.find((quote) => quote.source === Quoting.KitCoach)?.id;
  }

  if (!selectedQuote) {
    return undefined;
  }

  const quoteCards = quotes as QuoteCardSource[];
  let selectedQuoteMatcher: (quote: QuoteCardSource) => boolean;

  switch (selectedQuote) {
    case SelectedQuoteType.DAT_RATEVIEW:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.DAT;
      break;
    case SelectedQuoteType.MCLEOD_LANE_HISTORY:
    case SelectedQuoteType.TURVO_LANE_HISTORY:
    case SelectedQuoteType.GLOBALTRANZ_LANE_HISTORY:
    case SelectedQuoteType.GLOBALTRANZ_TMS_LANE_HISTORY:
    case SelectedQuoteType.LANE_HISTORY:
      selectedQuoteMatcher = (quote) =>
        quote.source === TMS_LANE_HISTORY_SOURCE;
      break;
    case SelectedQuoteType.GS_NETWORK:
    case SelectedQuoteType.GS_BUYPOWER:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.Greenscreens;
      break;
    case SelectedQuoteType.TRUCKSTOP_POSTED:
    case SelectedQuoteType.TRUCKSTOP_BOOKED:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.TruckStop;
      break;
    case SelectedQuoteType.KENCO_FLASH_AVERAGE:
      selectedQuoteMatcher = (quote) =>
        quote.source === Quoting.KencoFlash &&
        quote.type === QuoteTypeInSource.KencoAverage;
      break;
    case SelectedQuoteType.SONAR:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.Sonar;
      break;
    case SelectedQuoteType.TRANSFIX:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.Transfix;
      break;
    case SelectedQuoteType.SUPER_DISPATCH:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.SuperDispatch;
      break;
    case SelectedQuoteType.TRUPAY:
      selectedQuoteMatcher = (quote) => quote.source === Quoting.TruPay;
      break;
    default:
      return undefined;
  }

  return quoteCards.find(selectedQuoteMatcher)?.id;
}
